/* Playthrough record — the per-game serialization format defined in
   briefs/playthrough.md (schema "palindragon-playthrough/v1").

   The record is purely client-side: written to localStorage after
   every committed action (guess submit, hint purchase, spaces
   purchase, win, loss) and read back on subsequent loads of the same
   puzzle id so the player resumes mid-run after a refresh / tab
   restore / browser restart instead of starting over.

   Outcomes:
     - "won" / "lost"  — terminal. /complete POSTs and the
                         Puzzle Finished beacon fire alongside the
                         localStorage write.
     - "abandoned"     — in-progress. Local-only; no beacon, no POST.
                         A subsequent load restores it as status
                         "playing", reconstructs the buffer from the
                         carry-forward greens of the last guess, and
                         parks the cursor at the first empty
                         left-half slot — the same state endReveal()
                         would leave behind after a non-terminal
                         commit.

   The wire shape is brief-canonical (turns array, hint as two
   consecutive entries) so a future server adjudicator can ingest it
   without translation. The in-memory game state (`guesses`,
   `hintPurchasedAt`, `status`) remains the runtime SoT — this module
   is the boundary between that and on-disk storage. */

import type { Puzzle, PuzzleAnalysis } from "./puzzle";
import { buildMask } from "./puzzle";
import type { Mark } from "./scoring";
import { scoreGuess } from "./scoring";

export const PLAYTHROUGH_SCHEMA = "palindragon-playthrough/v1";

export type GuessTurn = {
  type: "guess";
  /** Length = ceil(letters / 2). Unentered cells are "_". */
  half: string;
  /** Per-cell ASCII feedback, same length as `half`. Y/M/N → 🟩/🟨/⬛. */
  feedback: string;
};

export type HintTurn = { type: "hint" };

export type SpacesTurn = { type: "spaces" };

export type Turn = GuessTurn | HintTurn | SpacesTurn;

const HINT_COST = 2;
const SPACES_COST = 3;

export type Outcome = "won" | "lost" | "abandoned";

export type Playthrough = {
  schema: typeof PLAYTHROUGH_SCHEMA;
  /** Server-issued UUID v4 minted by GET /today, threaded through the
     game state machine and stamped on the persisted record. Required
     end-to-end (briefs/playthrough.md §"Schema"). */
  game_id: string;
  puzzle_number: number;
  date: string;
  mask: string;
  letters: number;
  max_guesses: number;
  turns: Turn[];
  outcome: Outcome;
  solution_normalized: string;
};

export type GuessRow = readonly (string | null)[];

const STORAGE_PREFIX = "palindragon-playthrough:";

function storageKey(puzzleId: number): string {
  return `${STORAGE_PREFIX}${puzzleId}`;
}

function halfLength(letterCount: number): number {
  return Math.ceil(letterCount / 2);
}

function markToFeedback(m: Mark): "Y" | "M" | "N" {
  if (m === "green") return "Y";
  if (m === "yellow") return "M";
  return "N"; // gray + empty both render N (per brief §"Feedback encoding")
}

function buildGuessTurn(
  guess: GuessRow,
  analysis: PuzzleAnalysis,
): GuessTurn {
  const sol = analysis.normalized.split("");
  const fullMarks = scoreGuess(guess, sol);
  const halfLen = halfLength(analysis.letterCount);
  let half = "";
  let feedback = "";
  for (let i = 0; i < halfLen; i++) {
    const ch = guess[i] ?? null;
    half += ch ?? "_";
    feedback += markToFeedback(fullMarks[i]!);
  }
  return { type: "guess", half, feedback };
}

/** Serialize the current game state into a Playthrough. Maps in-memory
   status to the brief's outcome enum:
     - "won"     → outcome: "won"      (terminal, all-Y last guess)
     - "lost"    → outcome: "lost"     (terminal, budget exhausted)
     - "playing" → outcome: "abandoned" (incremental save mid-run)

   Returns null only for empty trivially-unsaveable states (no
   committed turns at all) — in-progress writes happen on every
   committed action, not on raw signals.

   `gameId` is the server-issued UUID v4 returned by GET /today.
   The dev-mode local-puzzle path mints a `dev-` prefixed synthetic
   id (web/src/dev/puzzleSource.ts::mintDevGameId) so the field is
   always present end-to-end. */
export function buildPlaythrough(args: {
  puzzle: Puzzle;
  gameId: string;
  analysis: PuzzleAnalysis;
  guesses: readonly GuessRow[];
  hintPurchasedAt: number | null;
  spacesPurchasedAt: number | null;
  status: "playing" | "won" | "lost";
}): Playthrough | null {
  const {
    puzzle,
    gameId,
    analysis,
    guesses,
    hintPurchasedAt,
    spacesPurchasedAt,
    status,
  } = args;
  // Empty in-progress (no committed action): nothing meaningful to
  // serialize, skip the write so we don't pollute localStorage with
  // a zero-turn record. Terminal states always have at least one
  // turn (the winning guess or the budget-exhausting one).
  if (
    status === "playing" &&
    guesses.length === 0 &&
    hintPurchasedAt == null &&
    spacesPurchasedAt == null
  ) {
    return null;
  }

  // Slot-walk: each `*PurchasedAt` is a final-board-layout slot index.
  // Walk slots in order; emit a reveal turn when the current slot falls
  // inside that reveal's span, otherwise emit the next pending guess.
  // The contract matches share.ts and Board.tsx so the three layouts
  // stay in lockstep.
  const totalSlots =
    guesses.length +
    (hintPurchasedAt != null ? HINT_COST : 0) +
    (spacesPurchasedAt != null ? SPACES_COST : 0);
  const turns: Turn[] = [];
  let guessIdx = 0;
  let hintEmitted = 0;
  let spacesEmitted = 0;
  while (turns.length < totalSlots) {
    const slot = turns.length;
    if (
      hintPurchasedAt != null &&
      slot >= hintPurchasedAt &&
      hintEmitted < HINT_COST
    ) {
      turns.push({ type: "hint" });
      hintEmitted++;
      continue;
    }
    if (
      spacesPurchasedAt != null &&
      slot >= spacesPurchasedAt &&
      spacesEmitted < SPACES_COST
    ) {
      turns.push({ type: "spaces" });
      spacesEmitted++;
      continue;
    }
    if (guessIdx < guesses.length) {
      turns.push(buildGuessTurn(guesses[guessIdx]!, analysis));
      guessIdx++;
      continue;
    }
    // Defensive: totalSlots accounting should make this unreachable.
    break;
  }

  const outcome: Outcome =
    status === "won" ? "won" : status === "lost" ? "lost" : "abandoned";

  return {
    schema: PLAYTHROUGH_SCHEMA,
    game_id: gameId,
    puzzle_number: puzzle.number,
    date: puzzle.date,
    mask: buildMask(puzzle.text),
    letters: analysis.letterCount,
    max_guesses: puzzle.maxGuesses,
    turns,
    outcome,
    solution_normalized: analysis.normalized,
  };
}

/** Mirror a half (length = ceil(letterCount/2)) into the full-length
   guess row. Returns null on length mismatch. Underscores become null. */
function halfToFullGuess(
  half: string,
  letterCount: number,
): (string | null)[] | null {
  if (half.length !== halfLength(letterCount)) return null;
  const out: (string | null)[] = new Array(letterCount).fill(null);
  for (let i = 0; i < half.length; i++) {
    const c = half[i]!;
    const value = c === "_" ? null : c;
    out[i] = value;
    out[letterCount - 1 - i] = value;
  }
  return out;
}

export type AppliedPlaythrough = {
  guesses: GuessRow[];
  hintPurchasedAt: number | null;
  spacesPurchasedAt: number | null;
  /** "playing" mirrors a stored "abandoned" outcome — the player
     resumes mid-run. The caller is responsible for rebuilding
     buffer + cursor from the carry-forward greens of `guesses`. */
  status: "won" | "lost" | "playing";
};

/** Decode a Playthrough into the signal-shaped state. Returns null
   when the record is malformed or doesn't match the supplied analysis
   (puzzle id collision with a different solution, schema drift, etc.).
   Validation here mirrors briefs/playthrough.md §"Validation rules"
   without the canonical re-derivation step (we trust scoreGuess to
   produce matching marks at render time).

   The recorded `hintPurchasedAt` / `spacesPurchasedAt` are the
   turns-array indices of the first reveal entry — the same value the
   in-memory state writes when the player buys (final-board-layout
   slot, since each turn is exactly one row). */
export function applyPlaythrough(
  p: Playthrough,
  analysis: PuzzleAnalysis,
): AppliedPlaythrough | null {
  if (p.schema !== PLAYTHROUGH_SCHEMA) return null;
  if (p.letters !== analysis.letterCount) return null;
  if (p.solution_normalized !== analysis.normalized) return null;
  if (
    p.outcome !== "won" &&
    p.outcome !== "lost" &&
    p.outcome !== "abandoned"
  ) {
    return null;
  }

  const restored: (string | null)[][] = [];
  let hintAt: number | null = null;
  let spacesAt: number | null = null;
  let i = 0;
  while (i < p.turns.length) {
    const turn = p.turns[i]!;
    if (turn.type === "hint") {
      // Rule 7: hint entries appear in consecutive pairs of exactly two,
      // at most one such pair per playthrough.
      if (hintAt != null) return null;
      if (i + 1 >= p.turns.length || p.turns[i + 1]!.type !== "hint") {
        return null;
      }
      hintAt = i;
      i += 2;
      continue;
    }
    if (turn.type === "spaces") {
      // Rule 7a: spaces entries appear in consecutive triples of exactly
      // three, at most one such triple per playthrough.
      if (spacesAt != null) return null;
      if (
        i + 2 >= p.turns.length ||
        p.turns[i + 1]!.type !== "spaces" ||
        p.turns[i + 2]!.type !== "spaces"
      ) {
        return null;
      }
      spacesAt = i;
      i += 3;
      continue;
    }
    const full = halfToFullGuess(turn.half, analysis.letterCount);
    if (full == null) return null;
    restored.push(full);
    i++;
  }

  return {
    guesses: restored,
    hintPurchasedAt: hintAt,
    spacesPurchasedAt: spacesAt,
    status: p.outcome === "abandoned" ? "playing" : p.outcome,
  };
}

/** Persist a playthrough to localStorage, keyed by puzzle id. Both
   terminal ("won"/"lost") and in-progress ("abandoned") records are
   written so the player can resume mid-run after a refresh.
   Silent no-op when storage is unavailable (private mode, quota,
   disabled), matching the project's existing theme.ts pattern. */
export function savePlaythrough(p: Playthrough): void {
  try {
    localStorage.setItem(storageKey(p.puzzle_number), JSON.stringify(p));
  } catch {
    /* private mode / disabled storage — in-memory only */
  }
}

/** Load a previously-saved playthrough by puzzle id, or null if there
   isn't one (or the record is unreadable). Schema-mismatch records
   are treated as absent so a future v2 ships without breaking old
   players' stored runs — they just lose the historical view, which
   is acceptable. */
export function loadPlaythrough(puzzleId: number): Playthrough | null {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(storageKey(puzzleId));
  } catch {
    return null;
  }
  if (raw == null) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object") return null;
  const candidate = parsed as Playthrough;
  if (candidate.schema !== PLAYTHROUGH_SCHEMA) return null;
  if (typeof candidate.puzzle_number !== "number") return null;
  if (!Array.isArray(candidate.turns)) return null;
  // game_id became required end-to-end in Phase 1.5. Pre-game_id
  // localStorage records (saved before the rollout) gracefully
  // invalidate to null so the player gets a fresh game rather than
  // a half-shaped restore.
  if (typeof candidate.game_id !== "string" || candidate.game_id.length === 0) {
    return null;
  }
  return candidate;
}

/** Erase a stored playthrough — exposed primarily for tests. */
export function clearPlaythrough(puzzleId: number): void {
  try {
    localStorage.removeItem(storageKey(puzzleId));
  } catch {
    /* same as save */
  }
}
